/**
 * The transient writing: subtitles, toasts and the level card.
 *
 * Subtitles matter more in a headset than they do on a monitor. The hound's
 * counterplay — stop moving, get low — is said out loud exactly once per run
 * and is otherwise undiscoverable, and in VR a player is usually facing
 * whatever is about to kill them rather than reading a corner of a screen. So
 * the subtitle panel sits low and central where a film's would, and stays for
 * the same number of seconds the DOM one does.
 */

import { CanvasPanel } from './panel';
import {
  AMBER,
  AMBER_DIM,
  AMBER_FAINT,
  BAD,
  clear,
  face,
  fitPx,
  GOOD,
  LINE,
  LINE_FAINT,
  measure,
  rule,
  text,
  wrap,
} from './theme';

/** Mirrors `MAX_TOASTS`, `TOAST_LIFE` and `TOAST_FADE` in `ui/hud.ts`. */
const MAX_TOASTS = 4;
const TOAST_LIFE = 3.4;
const TOAST_FADE = 0.5;

export type Tone = 'info' | 'good' | 'bad';

const TONE_COLOUR: Record<Tone, string> = { info: AMBER_DIM, good: GOOD, bad: BAD };

/* ------------------------------------------------------------------ *
 * Subtitles
 * ------------------------------------------------------------------ */

export class SubtitlePanel {
  readonly panel = new CanvasPanel(0.92, 0.2);

  private line = '';
  private speaker = '';
  private time = 0;
  private enabled = true;
  private alpha = 0;

  constructor() {
    this.panel.setOpacity(0);
  }

  /** One `ui:subtitle`. The duration rule is `ui/hud.ts`'s, to the millisecond. */
  say(line: string, ms?: number, speaker?: string): void {
    if (!this.enabled) return;
    this.line = line;
    this.speaker = speaker ?? '';
    this.time = (ms ?? Math.min(7000, 1500 + line.length * 55)) / 1000;
    this.draw();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.time = 0;
  }

  /**
   * Take the line away now. Used when a run starts, and when a session starts —
   * a subtitle said while the headset was off has no business waiting in the
   * room for somebody who has just put it on.
   */
  clear(): void {
    this.time = 0;
  }

  /** `dt` is simulation time, so a line does not expire behind a pause menu. */
  tick(dt: number): void {
    if (this.time > 0 && dt > 0) this.time = Math.max(0, this.time - dt);
  }

  update(dt: number, visible: boolean): void {
    const target = visible && this.time > 0 ? 1 : 0;
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / 0.22);
    this.panel.setOpacity(this.alpha);
  }

  dispose(): void {
    this.panel.dispose();
  }

  private draw(): void {
    const p = this.panel;
    p.paint((c, w, h) => {
      // No border and no ground under the speaker's name: a subtitle is words
      // over the room, not a sign in it.
      clear(c, w, h);
      const pad = p.px(0.03);
      const size = p.px(0.032);
      const style = { px: size, color: AMBER, align: 'center' as const };
      const lines = wrap(c, this.line, w - pad * 2, style);
      const lead = size * 1.42;
      const speakerH = this.speaker ? lead * 0.9 : 0;
      let y = (h - (lines.length - 1) * lead - speakerH) / 2;

      if (this.speaker) {
        text(c, `${this.speaker.toUpperCase()}:`, w / 2, y, {
          px: p.px(0.021),
          color: AMBER_FAINT,
          align: 'center',
          tracking: p.px(0.0016),
        });
        y += speakerH;
      }
      // A shadow plate behind each line, so amber over a lit yellow wall is
      // still amber. Drawn per line rather than as one block so short lines do
      // not carry a wide bar across the room.
      for (const l of lines) {
        const lineStyle = { ...style, px: fitPx(c, l, w - pad * 2, style) };
        const width = Math.min(w - pad, p.px(0.02) * 2 + measure(c, l, lineStyle));
        c.globalAlpha = 1;
        c.fillStyle = 'rgba(6, 5, 4, 0.72)';
        c.fillRect((w - width) / 2, y - lead * 0.55, width, lead * 1.1);
        text(c, l, w / 2, y, lineStyle);
        y += lead;
      }
    });
  }
}

/* ------------------------------------------------------------------ *
 * Toasts
 * ------------------------------------------------------------------ */

interface Toast {
  text: string;
  tone: Tone;
  /** Seconds left of life, then of fade. */
  t: number;
  fading: boolean;
  /** Wall clock at arrival, so a toast raised outside a session can be aged. */
  at: number;
}

export class ToastPanel {
  readonly panel = new CanvasPanel(0.44, 0.28);

  private readonly toasts: Toast[] = [];
  private dirty = false;
  private alpha = 0;

  constructor() {
    this.panel.setOpacity(0);
  }

  /**
   * One `ui:toast`. Recorded even when no session is running: a player who puts
   * the headset on mid-run should not be told about the granola bar they picked
   * up two minutes ago, which is what `prune` is for.
   */
  add(line: string, tone: Tone): void {
    this.toasts.push({ text: line, tone, t: TOAST_LIFE, fading: false, at: performance.now() });
    while (this.toasts.length > MAX_TOASTS) this.toasts.shift();
    this.dirty = true;
  }

  /** Drop anything that would already have gone. Called on entering a session. */
  prune(): void {
    const now = performance.now();
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const age = (now - this.toasts[i]!.at) / 1000;
      if (age > TOAST_LIFE + TOAST_FADE) this.toasts.splice(i, 1);
    }
    this.dirty = true;
  }

  clear(): void {
    this.toasts.length = 0;
    this.dirty = true;
  }

  /** `dt` is simulation time, matching the DOM stack. */
  tick(dt: number): void {
    if (dt <= 0) return;
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const t = this.toasts[i]!;
      t.t -= dt;
      if (t.t > 0) continue;
      if (!t.fading) {
        t.fading = true;
        t.t = TOAST_FADE;
      } else {
        this.toasts.splice(i, 1);
      }
      this.dirty = true;
    }
  }

  update(dt: number, visible: boolean): void {
    if (this.dirty) {
      this.dirty = false;
      this.draw();
    }
    const target = visible && this.toasts.length > 0 ? 1 : 0;
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / 0.22);
    this.panel.setOpacity(this.alpha);
  }

  dispose(): void {
    this.panel.dispose();
  }

  private draw(): void {
    const p = this.panel;
    p.paint((c, w, h) => {
      clear(c, w, h);
      if (this.toasts.length === 0) return;
      const pad = p.px(0.018);
      const size = p.px(0.023);
      const lead = size * 1.35;
      let y = pad + lead / 2;
      for (const t of this.toasts) {
        const style = { px: size, color: TONE_COLOUR[t.tone] };
        const lines = wrap(c, t.text, w - pad * 2 - p.px(0.014), style);
        const blockH = lines.length * lead;
        c.globalAlpha = 1;
        c.fillStyle = 'rgba(6, 5, 4, 0.82)';
        c.fillRect(0, y - lead * 0.6, w, blockH + lead * 0.2);
        c.fillStyle = TONE_COLOUR[t.tone];
        c.fillRect(0, y - lead * 0.6, p.px(0.004), blockH + lead * 0.2);
        for (const l of lines) {
          text(c, l, pad, y, { ...style, alpha: t.fading ? Math.max(0, t.t / TOAST_FADE) : 1 });
          y += lead;
        }
        y += lead * 0.5;
      }
    });
  }
}

/* ------------------------------------------------------------------ *
 * The level card
 * ------------------------------------------------------------------ */

/**
 * The name of the floor, resolving out of the dark.
 *
 * The DOM card is pure CSS animation on a timer; there is no timer here because
 * there does not need to be one. `ui/ui.ts` already holds `current` on
 * `'levelcard'` for exactly as long as the card is playing and puts it back to
 * `'hud'` when it finishes, so this panel simply follows that flag.
 */
export class LevelCardPanel {
  readonly panel = new CanvasPanel(0.9, 0.34);

  private key = '';
  private alpha = 0;

  constructor() {
    this.panel.setOpacity(0);
  }

  set(name: string, subtitle: string, blurb: string): void {
    const key = `${name}|${subtitle}`;
    if (key === this.key) return;
    this.key = key;
    this.draw(name, subtitle, blurb);
  }

  update(dt: number, visible: boolean): void {
    const target = visible && this.key ? 1 : 0;
    // Slower than the readouts. A level card is a beat, not a notification.
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / 0.9);
    this.panel.setOpacity(this.alpha);
  }

  dispose(): void {
    this.panel.dispose();
  }

  private draw(name: string, subtitle: string, blurb: string): void {
    const p = this.panel;
    p.paint((c, w, h) => {
      face(c, w, h, LINE_FAINT);
      const pad = p.px(0.045);
      let y = pad + p.px(0.02);
      text(c, name.toUpperCase(), w / 2, y, {
        px: p.px(0.021),
        color: AMBER_FAINT,
        align: 'center',
        tracking: p.px(0.006),
      });
      y += p.px(0.055);
      const big = { px: p.px(0.055), color: AMBER, align: 'center' as const, tracking: p.px(0.004) };
      text(c, subtitle.toUpperCase(), w / 2, y, { ...big, px: fitPx(c, subtitle.toUpperCase(), w - pad * 2, big) });
      y += p.px(0.05);
      rule(c, pad * 2, y, w - pad * 4, LINE);
      y += p.px(0.038);
      const body = { px: p.px(0.024), color: AMBER_DIM, align: 'center' as const };
      for (const l of wrap(c, blurb, w - pad * 2, body)) {
        text(c, l, w / 2, y, body);
        y += p.px(0.034);
      }
    });
  }
}
